import { POINTER_COLORS, type Frame } from '@praesto/shared'
import { useText } from '../i18n.js'
import type { Presentation } from './usePresentation.js'

/** Steuerleiste des Präsentierenden. */
export function PresentationBar({
  presentation,
  frames,
  viewerCount,
  followerCount,
}: {
  presentation: Presentation
  frames: Frame[]
  viewerCount: number
  followerCount: number
}): React.ReactElement {
  const t = useText()
  const current = frames[presentation.frameIndex]

  return (
    <div className="present-bar">
      <button
        className="button ghost icon"
        onClick={presentation.previous}
        disabled={presentation.frameIndex === 0 || frames.length === 0}
        aria-label={t('present.previous')}
      >
        ‹
      </button>

      <span className="present-frame">
        {frames.length === 0 ? (
          <span className="muted">{t('present.noFrames')}</span>
        ) : (
          <>
            <span className="present-counter">
              {presentation.frameIndex + 1}/{frames.length}
            </span>
            {current?.name}
          </>
        )}
      </span>

      <button
        className="button ghost icon"
        onClick={presentation.next}
        disabled={presentation.frameIndex >= frames.length - 1}
        aria-label={t('present.next')}
      >
        ›
      </button>

      <span className="present-divider" />

      <button
        className={`button ghost${presentation.freeNavigation ? ' is-on' : ''}`}
        onClick={presentation.toggleFreeNavigation}
        title={
          presentation.freeNavigation ? t('present.lockTitle') : t('present.unlockTitle')
        }
      >
        {presentation.freeNavigation ? `🔓 ${t('present.free')}` : `🔒 ${t('present.fixed')}`}
      </button>

      <span className="present-divider" />

      {/*
        Zeiger: ein Knopf zum Aus- und Einschalten, daneben die Farben. Die
        Farbwahl steht nur offen, solange der Zeiger an ist — sonst wählte man
        die Farbe von etwas Unsichtbarem.
      */}
      <span className="present-pointer">
        <button
          className={`button ghost${presentation.pointer ? ' is-on' : ''}`}
          onClick={presentation.togglePointer}
          title={
            presentation.pointer ? t('present.pointerOffTitle') : t('present.pointerOnTitle')
          }
        >
          <span
            className="pointer-swatch"
            style={{ backgroundColor: presentation.pointer ?? 'transparent' }}
            aria-hidden="true"
          />
          {t('present.pointer')}
        </button>

        {presentation.pointer && (
          <span className="pointer-colors">
            {POINTER_COLORS.map((color) => (
              <button
                key={color}
                className={`pointer-color${presentation.pointer === color ? ' is-on' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => presentation.setPointerColor(color)}
                title={t('present.pointerColorTitle')}
                aria-label={t('present.pointerColorLabel', { color })}
              />
            ))}
          </span>
        )}
      </span>

      <span className="present-divider" />

      <span className="muted small" title={t('present.followersTitle')}>
        {viewerCount === 0
          ? t('present.nobodyConnected')
          : t('present.following', { followers: followerCount, viewers: viewerCount })}
      </span>

      <button className="button ghost danger" onClick={presentation.stop}>
        {t('present.stop')}
      </button>
    </div>
  )
}

/** Hinweisleiste für alle, die einer laufenden Präsentation zusehen. */
export function FollowBar({ presentation }: { presentation: Presentation }): React.ReactElement {
  const t = useText()
  const name = presentation.presenter?.user.name ?? t('present.someone')

  return (
    <div className="follow-bar">
      <span className="follow-dot" style={{ backgroundColor: presentation.presenter?.user.color }} />
      {presentation.following ? (
        <span>{t('present.isPresenting', { name })}</span>
      ) : (
        <>
          <span className="muted">{t('present.lookingAround')}</span>
          <button className="button primary" onClick={presentation.follow}>
            {t('present.followAgain')}
          </button>
        </>
      )}
    </div>
  )
}
